import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewEncapsulation, ChangeDetectorRef, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { GridApi, ColDef, ColumnApi } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Models
import { BatteryLogEntry, VehicleLogTableItem, PilotLogTableItem } from '@mocca-technologies/model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default
})

export class DataTableComponent implements OnInit, OnDestroy, OnChanges {

  /**
   * Input parameters
   */

  @Input() columnsInput: ColDef[];
  @Input() logType;
  @Input() rows: PilotLogTableItem[] | VehicleLogTableItem[] | BatteryLogEntry[];
  @Input() components;
  @Input() context;
  @Input() paginationPageSize = 5;
  @Input() paginationPageSizeSelector = [5, 10, 50];
  @Input() fixedRowsPerPage: number;

  /**
   * Variables
   */

  columns = [];
  temp = [];
  hidden=true;

  editing = {};

  pagination = true;
  domLayout: 'normal' = 'normal';
  rowHeight = 36;
  headerHeight = 38;

  gridApi: GridApi;
  gridColumnApi: ColumnApi;

  autoSizeStrategy;

  fileName = null;

  localeText: any;
  gridKey = 0; // Key to force grid recreation

  get gridHeightPx(): number {
    const rowsPerPage = this.fixedRowsPerPage || this.paginationPageSize || 5;
    const paginationPanelHeight = 38;
    const horizontalScrollHeight = 12;

    return (rowsPerPage * this.rowHeight) + this.headerHeight + paginationPanelHeight + horizontalScrollHeight;
  }

  private resizeListener: any;
  private destroy$ = new Subject<void>();

  constructor(private translateService: TranslateService, private cdr: ChangeDetectorRef, private ngZone: NgZone) {

  }

  // #region --> Lifecycle Methods

  ngOnInit(): void {
    this.autoSizeStrategy = {
      type: 'fitGridWidth',
    };

    // Initialize locale text
    this.updateLocaleText();

    // Subscribe to language change events
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ngZone.run(() => {
          this.updateLocaleText();
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update grid when rows or columnsInput change
    if ((changes['rows'] || changes['columnsInput']) && this.gridApi) {
      if (changes['rows']) {
        this.updateGridData();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  private updateLocaleText(): void {
    this.ngZone.run(() => {
      this.localeText = {
        page: this.translateService.instant('COMMON.DATATABLE.PAGE'),
        of: this.translateService.instant('COMMON.DATATABLE.OF'),
        to: this.translateService.instant('COMMON.DATATABLE.TO'),
        first: this.translateService.instant('COMMON.DATATABLE.FIRST'),
        previous: this.translateService.instant('COMMON.DATATABLE.PREVIOUS'),
        next: this.translateService.instant('COMMON.DATATABLE.NEXT'),
        last: this.translateService.instant('COMMON.DATATABLE.LAST'),
        pageSizeSelectorLabel: this.translateService.instant('COMMON.DATATABLE.PAGE_SIZE'),
        noRowsToShow: this.translateService.instant('COMMON.DATATABLE.NO_ROWS')
      };
      
      // Force grid recreation by toggling gridKey
      this.gridKey = -1;
      this.cdr.detectChanges();
      
      setTimeout(() => {
        this.gridKey = 0;
        this.cdr.detectChanges();
      }, 50);
    });
  }

  // #endregion --> Lifecycle Methods

  // #region --> Datatable Methods

  onGridReady(params) {

    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.updateGridData();

    this.resizeListener = () => {
      if (this.gridApi) {
        // Optional: add a small delay to ensure DOM is fully updated
        setTimeout(() => {
          // Don't auto-fit columns, let them use their natural width
          // this.gridApi.sizeColumnsToFit();
        }, 100);
      }
    };

    // Add the event listener
    window.addEventListener('resize', this.resizeListener);

  }

  updateGridData(){

    if (this.gridApi) {
      // Clear grid data and update with new rows
      this.gridApi.setRowData(this.rows && this.rows.length > 0 ? this.rows : []);
    }

  }

  // #endregion --> Datatable Methods

}
